import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase }  from '../supabase.js';
import { pageQuery } from './egress.js';

/* ============================================================
   Weekly attendance register PDF — A4 landscape, matches the
   Londmarx UIF format (header block + 16-row signature grid +
   municipality footer). Embeds signature PNGs from Storage.
   Multi-page if the class has >16 enrolled learners.
============================================================ */

const A4_LANDSCAPE = [842, 595];   // points
const MARGIN       = 28;
const ROWS_PER_PG  = 14;            // bigger rows = bigger signatures, fewer per page

const COL_W = {
  num:     22,
  name:    180,
  id:      88,
  day:     100,           // each Day 1..5
};
const ROW_H = 28;

export async function generateRegisterPdf({ classId, weekStart, preview = false, previewWindow = null }) {
  /* ---------- 1. Fetch data ---------- */
  const days = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const isoFrom = isoDate(days[0]);
  const isoTo   = isoDate(days[4]);

  const [{ data: orgRows }, classResp, { data: roster }, { data: attRows }] = await Promise.all([
    pageQuery('org_settings'),
    supabase.from('classes').select(`
      class_id, class_name, start_date, end_date, programme_id,
      programme:programmes(programme_name),
      facilitator:facilitators(full_name),
      venue:training_venues(
        training_venue_name,
        town:towns(town_name, municipality:municipalities(municipality_name))
      )
    `).eq('class_id', classId).single(),
    pageQuery('class_roster', {
      eq:    { class_id: classId, enrollment_status: 'enrolled' },
      order: 'surname',
      ascending: true,
    }),
    pageQuery('attendance', {
      select: 'student_uuid,session_date,status,signature_url',
      eq:  { class_id: classId },
      gte: { session_date: isoFrom },
      lte: { session_date: isoTo },
    }),
  ]);

  const org = orgRows[0];
  const cls = classResp.data;
  if (classResp.error || !cls) throw new Error('Class not found');

  /* ---------- 2. Download signatures in parallel ---------- */
  const byKey = {};
  for (const r of attRows) byKey[`${r.student_uuid}|${r.session_date}`] = r;

  // Fill missing signature_url from per-term student_signatures table
  if (cls.programme_id) {
    const studentUuids = roster.map(s => s.student_uuid);
    const { data: termSigs } = await supabase
      .from('student_signatures')
      .select('student_uuid, signature_url')
      .eq('programme_id', cls.programme_id)
      .in('student_uuid', studentUuids);
    const termSigByStudent = {};
    for (const t of (termSigs || [])) termSigByStudent[t.student_uuid] = t.signature_url;
    for (const r of attRows) {
      if (r.status === 'PRESENT' && !r.signature_url && termSigByStudent[r.student_uuid]) {
        r.signature_url = termSigByStudent[r.student_uuid];
      }
    }
  }

  const sigBytesByKey = {};
  await Promise.all(attRows
    .filter(r => r.status === 'PRESENT' && r.signature_url)
    .map(async (r) => {
      try {
        const resp = await fetch(r.signature_url);
        if (!resp.ok) { console.warn('signature fetch failed:', r.signature_url, resp.status); return; }
        sigBytesByKey[`${r.student_uuid}|${r.session_date}`] = await resp.arrayBuffer();
      } catch (err) {
        console.warn('signature fetch error:', r.signature_url, err.message);
      }
    }));

  /* ---------- 3. Build PDF ---------- */
  const pdf   = await PDFDocument.create();
  const helv  = await pdf.embedFont(StandardFonts.Helvetica);
  const helvB = await pdf.embedFont(StandardFonts.HelveticaBold);

  // Embed the Londmarx logo (served by Vite from app/public/)
  let logoImg = null;
  try {
    const logoResp  = await fetch('/londmarx%20logo.png');
    const logoBytes = await logoResp.arrayBuffer();
    logoImg = await pdf.embedPng(logoBytes);
  } catch (err) {
    console.warn('logo load failed, falling back to text:', err.message);
  }

  const sigImgByKey = {};
  for (const [key, bytes] of Object.entries(sigBytesByKey)) {
    try { sigImgByKey[key] = await pdf.embedPng(bytes); } catch { /* ignore bad PNGs */ }
  }

  const weekNum   = weekNumberOf(days[0], cls.start_date);
  const totalPgs  = Math.max(1, Math.ceil(roster.length / ROWS_PER_PG));
  const muniName  = cls.venue?.town?.municipality?.municipality_name || '';

  for (let pg = 0; pg < totalPgs; pg++) {
    const page  = pdf.addPage(A4_LANDSCAPE);
    const slice = roster.slice(pg * ROWS_PER_PG, (pg + 1) * ROWS_PER_PG);
    drawHeader(page, helv, helvB, { org, cls, days, weekNum, logoImg });
    drawGrid(page, helv, helvB, {
      roster: slice, days, byKey, sigImgByKey,
      pageIndex: pg, totalPages: totalPgs, startNumber: pg * ROWS_PER_PG + 1,
    });
    drawFooter(page, helv, { org, muniName, pageIndex: pg, totalPages: totalPgs });
  }

  /* ---------- 4. Output ---------- */
  const bytes = await pdf.save();
  const blob  = new Blob([bytes], { type: 'application/pdf' });
  const url   = URL.createObjectURL(blob);
  if (preview) {
    if (previewWindow && !previewWindow.closed) {
      previewWindow.location.href = url;
    } else {
      window.open(url, '_blank');
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = `register-${slug(cls.class_name)}-week-of-${isoFrom}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/* ============================================================
   Page sections
============================================================ */

function drawHeader(page, helv, helvB, { org, cls, days, weekNum, logoImg }) {
  const { width, height } = page.getSize();
  let y = height - MARGIN;

  // Title
  page.drawText(`ATTENDANCE REGISTER: ${org.programme_period_label || ''}.`, {
    x: MARGIN, y: y - 12, size: 12, font: helvB,
  });

  // Logo (top-right) — embedded if available, else fallback text
  if (logoImg) {
    const logoH = 50;
    const dim   = logoImg.scaleToFit(120, logoH);
    page.drawImage(logoImg, {
      x: width - MARGIN - dim.width,
      y: y - dim.height + 6,
      width: dim.width, height: dim.height,
    });
  } else {
    page.drawText('LONDMARX', {
      x: width - MARGIN - 64, y: y - 12, size: 12, font: helvB, color: rgb(0.85, 0.16, 0.07),
    });
    page.drawText('GROUP', {
      x: width - MARGIN - 38, y: y - 24, size: 7, font: helvB, color: rgb(0.5, 0.5, 0.5),
    });
  }

  y -= 42;

  const left = [
    ['COMPANY NAME',         org.company_name],
    ['PROGRAMME NAME',       cls.programme?.programme_name || ''],
    ['IMPLEMENTING PARTNER', org.implementing_partner_name],
    ['FACILITATOR',          cls.facilitator?.full_name || org.default_training_provider],
    ['PERIOD OF TRAINING',   periodOf(cls.start_date, cls.end_date)],
  ];
  const right = [
    ['CONTACT PERSON', org.contact_person],
    ['CONTACT NUMBER', org.contact_number],
    ['EMAIL ADDRESS',  org.contact_email],
    ['VENUE',          cls.venue?.training_venue_name || ''],
    ['WEEK',           `${weekNum}      ${shortDate(days[0])} – ${shortDate(days[4])}  ${days[4].getFullYear()}`],
  ];

  for (let i = 0; i < 5; i++) {
    const [lLab, lVal] = left[i];
    const [rLab, rVal] = right[i];
    page.drawText(lLab, { x: MARGIN,           y, size: 7.5, font: helvB });
    page.drawText(lVal || '', { x: MARGIN + 110, y, size: 9,   font: helv });
    page.drawText(rLab, { x: width / 2,        y, size: 7.5, font: helvB });
    page.drawText(rVal || '', { x: width / 2 + 90, y, size: 9, font: helv });
    y -= 16;
  }
}

function drawGrid(page, helv, helvB, { roster, days, byKey, sigImgByKey, pageIndex, totalPages, startNumber }) {
  const { width, height } = page.getSize();
  const top = height - MARGIN - 140;       // below header
  const left = MARGIN;

  const colXs = [
    left,
    left + COL_W.num,
    left + COL_W.num + COL_W.name,
    left + COL_W.num + COL_W.name + COL_W.id,
    left + COL_W.num + COL_W.name + COL_W.id + COL_W.day,
    left + COL_W.num + COL_W.name + COL_W.id + COL_W.day * 2,
    left + COL_W.num + COL_W.name + COL_W.id + COL_W.day * 3,
    left + COL_W.num + COL_W.name + COL_W.id + COL_W.day * 4,
    left + COL_W.num + COL_W.name + COL_W.id + COL_W.day * 5,
  ];

  // Header row
  drawCell(page, helvB, '#',                            colXs[0], top - ROW_H, COL_W.num,  ROW_H, 7);
  drawCell(page, helvB, 'LEARNER SURNAME AND NAME',     colXs[1], top - ROW_H, COL_W.name, ROW_H, 7);
  drawCell(page, helvB, 'ID NUMBER',                    colXs[2], top - ROW_H, COL_W.id,   ROW_H, 7);
  // Combined "LEARNER SIGNATURE" header spanning all 5 day columns
  rect(page, colXs[3], top - ROW_H / 2, COL_W.day * 5, ROW_H / 2);
  page.drawText('LEARNER SIGNATURE', {
    x: colXs[3] + COL_W.day * 2.5 - 50, y: top - ROW_H / 2 + 4, size: 7, font: helvB,
  });
  for (let i = 0; i < 5; i++) {
    rect(page, colXs[3 + i], top - ROW_H, COL_W.day, ROW_H / 2);
    page.drawText(`Day ${i + 1}`, {
      x: colXs[3 + i] + 30, y: top - ROW_H + 5, size: 7, font: helvB,
    });
  }

  // Body rows
  for (let r = 0; r < ROWS_PER_PG; r++) {
    const rowY = top - ROW_H * (r + 2);
    rect(page, colXs[0], rowY, COL_W.num,  ROW_H);
    rect(page, colXs[1], rowY, COL_W.name, ROW_H);
    rect(page, colXs[2], rowY, COL_W.id,   ROW_H);
    for (let i = 0; i < 5; i++) rect(page, colXs[3 + i], rowY, COL_W.day, ROW_H);

    const s = roster[r];
    if (!s) continue;
    const num = startNumber + r;
    page.drawText(String(num), { x: colXs[0] + 6, y: rowY + 7, size: 9, font: helv });
    page.drawText(`${(s.surname || '').toUpperCase()} ${s.first_name || ''}`.slice(0, 30), {
      x: colXs[1] + 4, y: rowY + 7, size: 8.5, font: helv,
    });
    page.drawText(s.national_id || '', { x: colXs[2] + 4, y: rowY + 7, size: 8.5, font: helv });

    for (let i = 0; i < 5; i++) {
      const day = days[i];
      const att = byKey[`${s.student_uuid}|${isoDate(day)}`];
      if (!att) continue;
      if (att.status === 'ABSENT') {
        page.drawText('A', { x: colXs[3 + i] + COL_W.day / 2 - 3, y: rowY + 6, size: 11, font: helvB, color: rgb(0, 0, 0) });
      } else if (att.status === 'PRESENT') {
        const img = sigImgByKey[`${s.student_uuid}|${isoDate(day)}`];
        if (img) {
          const dim = img.scaleToFit(COL_W.day - 8, ROW_H - 4);
          page.drawImage(img, {
            x: colXs[3 + i] + (COL_W.day - dim.width) / 2,
            y: rowY + (ROW_H - dim.height) / 2,
            width: dim.width, height: dim.height,
          });
        }
      }
    }
  }
}

function drawFooter(page, helv, { org, muniName, pageIndex, totalPages }) {
  const { width } = page.getSize();
  const note = `1.   Attendance Register- ${org.company_name}, Labour Activation Programme (LAP)${muniName ? ', ' + muniName : ''}.`;
  page.drawText(note, { x: MARGIN, y: 28, size: 8, font: helv });
  if (totalPages > 1) {
    page.drawText(`Page ${pageIndex + 1} of ${totalPages}`, {
      x: width - MARGIN - 70, y: 28, size: 8, font: helv,
    });
  }
}

/* ============================================================
   Helpers
============================================================ */
function rect(page, x, y, w, h) {
  page.drawRectangle({ x, y, width: w, height: h, borderColor: rgb(0, 0, 0), borderWidth: 0.5 });
}
function drawCell(page, font, text, x, y, w, h, size) {
  rect(page, x, y, w, h);
  page.drawText(text, { x: x + 4, y: y + h / 2 - 3, size, font });
}
function isoDate(d) {
  const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,'0'); const dd = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function shortDate(d)  { return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' }); }
function periodOf(s, e) {
  if (!s) return '';
  const sd = new Date(s); const ed = e ? new Date(e) : null;
  return ed ? `${shortDate(sd)} ${sd.getFullYear()} – ${shortDate(ed)} ${ed.getFullYear()}` : `${shortDate(sd)} ${sd.getFullYear()} – ongoing`;
}
function weekNumberOf(weekStart, classStart) {
  if (!classStart) return '';
  const start = new Date(classStart);
  const diffMs = weekStart - start;
  const weeks  = Math.floor(diffMs / (7 * 86400 * 1000)) + 1;
  return weeks > 0 ? String(weeks) : '1';
}
function slug(s) { return String(s || '').replace(/[^a-z0-9]+/gi, '-').toLowerCase(); }
